package dev.lingvo.site.pages.app.settings.agents

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import dev.lingvo.shared.AgentConnectionDto
import dev.lingvo.shared.CreateAgentConnectionRequest
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.copySafely
import dev.lingvo.site.i18n.t
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.runWorkspaceAction
import kotlinx.coroutines.launch

@Composable
internal fun CreateAgentConnectionAction(
    pageClaim: WorkspaceActionClaim,
    request: CreateAgentConnectionRequest,
    enabled: Boolean,
    busy: Boolean,
    setBusy: (Boolean) -> Unit,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    createAgent: suspend (String, CreateAgentConnectionRequest) -> AgentConnectionDto = ApiClient::createAgent,
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    onCreated: (AgentConnectionDto) -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }
    LButton(
        t("app.settings.agents.agent_connection_actions.create_connection", "Create connection"),
        {
            val claim = pageClaim
                .takeIf { it.remainsAuthorized(currentContext()) }
                ?: return@LButton
            launch {
                runWorkspaceAction(
                    claim = claim,
                    currentContext = currentContext,
                    setBusy = setBusy,
                    transport = { workspaceSlug -> createAgent(workspaceSlug, request) },
                    onSuccess = onCreated,
                    onFailure = onFailure,
                )
            }
        },
        enabled = enabled && !busy,
        loading = busy,
    )
}

@Composable
internal fun AgentCopyAndContinueAction(
    pageClaim: WorkspaceActionClaim,
    token: String,
    target: String,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    copyToken: suspend (String) -> Unit = { copySafely(token) },
    navigate: (String) -> Unit,
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    onCopied: () -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }
    var busy by remember(pageClaim.subject, target) { mutableStateOf(false) }
    LButton(
        t("app.settings.agents.agent_connection_actions.copy_and_continue", "Copy and continue"),
        {
            val claim = pageClaim
                .takeIf { it.remainsAuthorized(currentContext()) }
                ?: return@LButton
            launch {
                runWorkspaceAction(
                    claim = claim,
                    currentContext = currentContext,
                    setBusy = { busy = it },
                    transport = copyToken,
                    onSuccess = {
                        onCopied()
                        navigate(target)
                    },
                    onFailure = onFailure,
                )
            }
        },
        enabled = !busy,
        loading = busy,
    )
}
